import React from 'react';

/**
 * StatusFilterCards — shared across all dashboards and task lists.
 *
 * Props:
 *   stats: [{ label, value, color, filter }]
 *   activeFilter: string  — currently active filter value
 *   onFilter: (filter) => void  — called with '' to clear, or filter value to set
 */
export default function StatusFilterCards({ stats, activeFilter, onFilter, variant = 'default' }) {
  if (variant === 'accent') {
    return (
      <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-3 mb-6">
        {stats.map(({ label, value, color, filter, accent, icon }) => {
          const isActive = filter && activeFilter === filter;
          return (
            <button
              key={label}
              type="button"
              onClick={() => filter && onFilter(activeFilter === filter ? '' : filter)}
              className={`relative bg-white rounded-xl border p-4 text-left transition-all group ${
                filter ? 'hover:shadow-md hover:-translate-y-0.5 cursor-pointer' : 'cursor-default'
              } ${isActive ? 'ring-2 ring-[#7AA33E] border-[#7AA33E] shadow-lg -translate-y-0.5 bg-[#f6faf0]' : 'border-gray-200 hover:border-gray-300'}`}
            >
              <div className={`absolute left-0 top-0 bottom-0 w-1 rounded-l-xl ${accent || 'bg-gray-300'}`} />
              <div className="flex items-start justify-between gap-1 pl-1">
                <div>
                  <p className={`text-2xl leading-none ${isActive ? 'font-extrabold text-[#5a8a2a]' : 'font-bold text-gray-900'}`}>{value ?? 0}</p>
                  <p className={`text-xs mt-1.5 leading-tight ${isActive ? 'font-bold text-[#5a8a2a]' : 'font-medium text-gray-500'}`}>{label}</p>
                </div>
                {icon && <span className="text-lg opacity-60 group-hover:opacity-100 transition-opacity">{icon}</span>}
              </div>
            </button>
          );
        })}
      </div>
    );
  }

  // default variant — unchanged, used by Admin/Manager/Superuser dashboards
  return (
    <div className={`grid gap-3 mb-5`} style={{ gridTemplateColumns: `repeat(${stats.length}, minmax(0, 1fr))` }}>
      {stats.map(({ label, value, color, filter }) => {
        const isActive = filter && activeFilter === filter;
        return (
          <button
            key={label}
            type="button"
            onClick={() => filter && onFilter(activeFilter === filter ? '' : filter)}
            className={`rounded-xl border p-4 text-left transition ${color} ${
              filter ? 'hover:shadow-sm cursor-pointer' : 'cursor-default'
            } ${isActive ? 'ring-2 ring-[#7AA33E] shadow-sm' : ''}`}
          >
            <p className="text-2xl font-bold">{value}</p>
            <p className="text-xs font-medium mt-0.5">{label}</p>
          </button>
        );
      })}
    </div>
  );
}
